<template>
  <div class="chat-widget-template-gallery">
    <div class="chat-widget-template-gallery__header">
      <div class="chat-widget-template-gallery__toggle-links">
        <a
          :class="{ 'chat-widget-template-gallery__link--active': allSelected }"
          @click="selectAll"
        >All</a>
        <span class="chat-widget-template-gallery__separator">&middot;</span>
        <a @click="clearAll">Clear</a>
      </div>
      <div class="chat-widget-template-gallery__counter">
        {{ selectedIds.length }} selected
      </div>
    </div>

    <div class="chat-widget-template-gallery__grid">
      <div
        v-for="tpl in templates"
        :key="tpl.id"
        :class="[
          'chat-widget-template-gallery__card',
          { 'chat-widget-template-gallery__card--selected': isSelected(tpl.id) }
        ]"
        @click.stop.prevent="toggleSelect(tpl.id)"
      >
        <div class="chat-widget-template-gallery__thumb">
          <button
            type="button"
            class="chat-widget-template-gallery__preview"
            title="Preview"
            aria-label="Preview template"
            @click.stop="openPreview(tpl)"
          >
            <i class="bi bi-arrows-angle-expand" />
          </button>
          <img
            :src="tpl.thumbnail"
            :alt="tpl.name"
            class="chat-widget-template-gallery__img"
            @click.stop.prevent="toggleSelect(tpl.id)"
          >
          <div
            v-if="isSelected(tpl.id)"
            class="chat-widget-template-gallery__check"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
            </svg>
          </div>
        </div>
        <div class="chat-widget-template-gallery__name">
          {{ getStyleLabel(tpl) || tpl.name }}
        </div>
      </div>
    </div>

    <div class="chat-widget-template-gallery__footer">
      <button
        class="chat-widget-template-gallery__create-btn"
        :disabled="disabled || selectedIds.length === 0"
        @click="onCreateImages"
      >
        Create {{ selectedIds.length }} {{ selectedIds.length === 1 ? 'image' : 'images' }}
        <span class="chat-widget-template-gallery__arrow">&rarr;</span>
      </button>
      <a
        class="chat-widget-template-gallery__browse-link"
        @click="$emit('action', 'browse_gallery')"
      >
        Browse in gallery <span>&rarr;</span>
      </a>
    </div>
  </div>
</template>

<script>
import {
  getStyleKeys,
  styleKeyToLabel,
} from '@frontend/store/creative-studio/template-showcase-tags';

export default {
  name: 'AIChatWidgetTemplateGallery',
  props: {
    templates: {
      type: Array,
      required: true,
    },
    preselectedIds: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedIds: [...this.preselectedIds],
    };
  },
  computed: {
    allSelected() {
      return this.selectedIds.length === this.templates.length;
    },
  },
  methods: {
    getStyleLabel(tpl) {
      const key = getStyleKeys(tpl.tags)[0] || '';
      return key ? styleKeyToLabel(key) : '';
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleSelect(id) {
      if (this.disabled) return;
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },
    openPreview(tpl) {
      const src = tpl && tpl.thumbnail;
      if (!src) return;
      this.$emit('action', 'networks_lightbox:' + encodeURIComponent(src));
    },
    selectAll() {
      if (this.disabled) return;
      this.selectedIds = this.templates.map((t) => t.id);
    },
    clearAll() {
      if (this.disabled) return;
      this.selectedIds = [];
    },
    onCreateImages() {
      if (!this.selectedIds.length || this.disabled) return;
      this.$emit('action', 'create_pdp_images:' + this.selectedIds.join(','));
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-widget-template-gallery {
  margin-top: 10px;
  width: 0;
  min-width: 100%;
}

.chat-widget-template-gallery__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}

.chat-widget-template-gallery__toggle-links {
  display: flex;
  align-items: center;
  gap: 6px;

  a {
    color: #379C75;
    cursor: pointer;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &.chat-widget-template-gallery__link--active {
      color: #379C75;
    }
  }
}

.chat-widget-template-gallery__separator {
  color: #94a3b8;
}

.chat-widget-template-gallery__counter {
  color: #64748b;
  font-weight: 500;
}

.chat-widget-template-gallery__grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.chat-widget-template-gallery__card {
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #379C75;
    box-shadow: 0 4px 16px rgba(55, 156, 117, 0.15);
  }

  &--selected {
    border-color: #379C75;
  }
}

.chat-widget-template-gallery__thumb {
  position: relative;
  width: 100%;
  height: 100px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.chat-widget-template-gallery__preview {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-2px);
  transition: opacity 0.15s ease, transform 0.15s ease, background 0.15s ease;
  z-index: 3;
  pointer-events: none;

  i {
    font-size: 14px;
    line-height: 1;
  }

  &:hover {
    background: #0f172a;
    color: #fff;
  }
}

.chat-widget-template-gallery__card:hover .chat-widget-template-gallery__preview {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
}

.chat-widget-template-gallery__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.chat-widget-template-gallery__check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #379C75;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.chat-widget-template-gallery__name {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  padding: 6px 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-widget-template-gallery__footer {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
}

.chat-widget-template-gallery__create-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #379C75;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #2E8D68;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.chat-widget-template-gallery__arrow {
  font-size: 15px;
}

.chat-widget-template-gallery__browse-link {
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #379C75;
    text-decoration: underline;
  }
}
</style>
